import React, { FC, useEffect, useState, useRef } from 'react'
import { useOrganization } from '../context/OrganizationContext';
import { useText } from '../context/TextContext';
import { Link, useNavigate, useParams } from 'react-router-dom';
import config from '../utils/config';
import { COLLECTIONS, DND_ACTIVATION_DELAY_CONSTRAINT, DND_ACTIVATION_DISTANCE_CONSTRAINT, ORGANIZATION_SETTINGS_TABS, PATHS, QUERY_PARAMS } from '../utils/shared/constants';
import { Deal } from '../models/shared/Deal';
import { useFirestore } from 'reactfire';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { DndContext, DragEndEvent, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import DealStepColumn from '../components/DealStepColumn';
import { DealCard } from '../components/DealCard';
import { runTransaction } from "firebase/firestore";
import { calculateNewOrder } from '../utils/logic';
import DealsBadge from '../components/DealsBadge';
import DealModal from '../components/modals/DealModal';
import { Organization } from '../models/shared/Organization';
import { DealFilters, useDeals } from '../hooks/useDeals';
import { DEFAULT_CARD_LAYOUT } from '../models/shared/CardLayout';
import useQueryParam from '../hooks/useQueryParams';
import PageHeader from '../components/PageHeader';
import { OverlayTrigger } from 'react-bootstrap';
import { AdvancedButton } from '../components/AdvancedButton';
import DealFiltersPicker from '../components/DealFiltersPicker';
import DealShareModal from '../components/modals/DealShareModal';
import UsageSection from '../components/UsageSection';
import { getActiveDealsCount } from '../utils/shared/deal';

interface IProps { }

export const PipelinePage: FC<IProps> = (props) => {
    const { dealId } = useParams<{ dealId: string }>();
    const { newDeal } = useQueryParam()
    const { organization, orgId, currentPlanDealLimit } = useOrganization();
    const [processing, setProcessing] = React.useState(false);
    const [filters, setFilters] = useState<DealFilters>({});
    const { allDeals, dealsPerId } = useDeals({ filters });
    const [dealIdsPerStep, setDealIdsPerStep] = useState<Record<string, string[]>>({});
    const [orphanedDeals, setOrphanedDeals] = useState<Deal[]>([]);
    const text = useText();
    const firestore = useFirestore();
    const [draggedDealId, setDraggedDealId] = useState('');
    const sensors = useSensors(
        useSensor(TouchSensor, { activationConstraint: DND_ACTIVATION_DELAY_CONSTRAINT }),
        useSensor(MouseSensor, { activationConstraint: DND_ACTIVATION_DISTANCE_CONSTRAINT }),
    );
    const navigate = useNavigate();
    const [dealToShare, setDealToShare] = useState<Deal | null>(null);

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const cardLayout = organization.cardLayout || DEFAULT_CARD_LAYOUT;

    useEffect(() => {
        setDealIdsPerStep(getDealsPerStep(organization, allDeals));
        setOrphanedDeals(getOrphanedDeals(organization, allDeals));
    }, [setDealIdsPerStep, allDeals, organization]);

    useEffect(() => {
        const checkScroll = () => {
            const container = scrollContainerRef.current;
            if (container) {
                setCanScrollLeft(container.scrollLeft > 0);
                setCanScrollRight(
                    container.scrollWidth > container.clientWidth &&
                    container.scrollLeft < container.scrollWidth - container.clientWidth
                );
            }
        };

        checkScroll();

        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
            window.addEventListener('resize', checkScroll);
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', checkScroll);
                window.removeEventListener('resize', checkScroll);
            }
        };
    }, [dealIdsPerStep, organization.dealSteps, orphanedDeals]);

    const handleScroll = (direction: 'left' | 'right') => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const columnElement = container.querySelector('[data-step-column]');
        let scrollAmount = 300;

        if (columnElement) {
            scrollAmount = columnElement.getBoundingClientRect().width + 24;
        }

        const currentScroll = container.scrollLeft;
        const newScroll = direction === 'left'
            ? currentScroll - scrollAmount
            : currentScroll + scrollAmount;

        container.scrollTo({
            left: newScroll,
            behavior: 'smooth'
        });
    };

    const handleNewDeal = async (stepId: string) => {
        setProcessing(true);

        const stepExistingDealIds = dealIdsPerStep[stepId] || [];
        const lastDeal = stepExistingDealIds.length ? dealsPerId[stepExistingDealIds[stepExistingDealIds.length - 1]] : null;
        const newDealOrder = lastDeal ? (lastDeal.order || 0) + 1 : 0;

        const newDeal: Deal = {
            name: text.newDeal,
            brandNew: true,
            status: 'active',
            stepId,
            createdAt: new Date(),
            updatedAt: new Date(),
            order: newDealOrder,
        }

        try {
            const newDealRef = await addDoc(collection(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}`), newDeal);
            newDeal.id = newDealRef.id;
            setDealIdsPerStep({ ...dealIdsPerStep, [stepId]: [...stepExistingDealIds, newDealRef.id] });
            navigate(`${PATHS.DEALS}/${newDealRef.id}?${QUERY_PARAMS.NEW_DEAL}=true`);
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }

        setProcessing(false);
    }

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        setDraggedDealId('');

        if (!over) {
            return;
        }

        const targetStepId = over.data?.current?.stepId;
        const useLastOrder = over.data?.current?.useLastOrder;
        const draggedDealId = active.id + '';
        const targetId = over.id + '';

        handleChangeDealStep(draggedDealId, targetId, targetStepId, useLastOrder);
    }

    const handleChangeDealStep = async (dealId: string, targetDealId: string, targetStepId: string, useLastOrder: boolean) => {
        const currentDealsOrder = dealIdsPerStep[targetStepId] || [];
        const validStepIds = new Set(organization.dealSteps.map(step => step.id));
        if (!validStepIds.has(targetStepId)) {
            return;
        }

        const targetDealsOrder = calculateNewOrder(currentDealsOrder, dealId, useLastOrder ? '' : targetDealId);

        const oldStepId = dealsPerId[dealId].stepId;
        const oldDealsOrder = dealIdsPerStep[oldStepId].filter(id => id !== dealId);
        setDealIdsPerStep({ ...dealIdsPerStep, [oldStepId]: oldDealsOrder, [targetStepId]: targetDealsOrder });

        await runTransaction(firestore, async (transaction) => {
            for (let ix = 0; ix < targetDealsOrder.length; ix++) {
                if (targetDealsOrder[ix] === currentDealsOrder[ix]) {
                    continue;
                }

                const dealUpdate: Partial<Deal> = {
                    order: ix + 1,
                    stepId: targetStepId,
                };
                const docRef = doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${targetDealsOrder[ix]}`);

                transaction.update(docRef, dealUpdate);
            }
        });
    }

    const handleDealArchive = async (deal: Deal) => {
        const dealUpdate: Partial<Deal> = {
            updatedAt: new Date(),
            status: 'archived',
        }
        const docRef = doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${deal.id}`);
        try {
            await updateDoc(docRef, dealUpdate);

            // Remove from regular step columns if present
            if (dealIdsPerStep[deal.stepId]) {
                const oldOrder = dealIdsPerStep[deal.stepId];
                const newOrder = oldOrder.filter(id => id !== deal.id);
                setDealIdsPerStep({ ...dealIdsPerStep, [deal.stepId]: newOrder });
            }

            // Remove from orphaned deals if present
            if (orphanedDeals.some(orphanedDeal => orphanedDeal.id === deal.id)) {
                setOrphanedDeals(orphanedDeals.filter(orphanedDeal => orphanedDeal.id !== deal.id));
            }
        } catch (error) {
            console.error(error);
            toast.error(text.defaultError);
        }
    }


    const draggedDeal = allDeals.find(deal => deal.id === draggedDealId);
    const editDeal = allDeals.find(deal => deal.id === dealId);
    const totalDeals = allDeals.length;
    const defaultStepId = organization.dealSteps[0]?.id || '';
    const hasFilter = filters?.steps?.length || filters?.tags?.length;
    const reachedDealLimit = currentPlanDealLimit !== -1 && getActiveDealsCount(allDeals) > currentPlanDealLimit;
    return (
        <>
            <div className="d-flex h-100 flex-column container-fluid">
                <PageHeader title={text.pipeline}
                    titleSuffix={<DealsBadge dealCount={totalDeals} />}
                    actions={[
                        {
                            processing,
                            text: text.newDeal,
                            icon: 'bi-plus',
                            disabled: reachedDealLimit,
                            onClick: () => handleNewDeal(defaultStepId)
                        },
                        {
                            text: text.editPipeline,
                            icon: 'bi-pencil',
                            link: PATHS.PIPELINE_EDIT
                        },
                    ]}
                    actionsPrefix={
                        <OverlayTrigger
                            placement='bottom-end'
                            trigger={['click']}
                            rootClose
                            overlay={
                                <div>
                                    <DealFiltersPicker filters={filters} onUpdateFilters={setFilters} />
                                </div>
                            }
                        >
                            <AdvancedButton className=''
                                variant={hasFilter ? 'secondary' : 'outline-secondary'} icon='bi-filter' />
                        </OverlayTrigger>
                    }
                />


                <div className="position-relative flex-1 d-flex flex-column gap-4">
                    {reachedDealLimit && <div className='alert alert-warning d-flex flex-column gap-2 mb-0' role='alert'>
                        <div className='d-flex align-items-center gap-2'>

                            <i className="bi bi-exclamation-triangle-fill"></i>
                            {/* No upgrade link in the native app - store rules don't allow pointing to payments outside the store */}
                            {config.isNativeApp
                                ? <span>{text.dealLimitReachedNative}</span>
                                : <Link to={`${PATHS.ORGANIZATIONSETTINGS}/?${QUERY_PARAMS.TAB}=${ORGANIZATION_SETTINGS_TABS.BILLING}`}>{text.dealLimitReached}</Link>}
                        </div>
                        <UsageSection activeDealsCount={getActiveDealsCount(allDeals)} planDealLimit={currentPlanDealLimit} />
                    </div>}
                    {canScrollLeft && (
                        <div className="position-fixed top-50 start-0 translate-middle-y ms-3 bg-white rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px', zIndex: 10, opacity: 0.8, cursor: 'pointer' }}
                            onClick={() => handleScroll('left')}>
                            <i className="bi bi-chevron-left fs-5"></i>
                        </div>
                    )}

                    <div className='d-flex flex-1 row m-0 flex-1 flex-nowrap gap-4'
                        ref={scrollContainerRef}
                        style={{
                            scrollSnapType: draggedDealId ? 'unset' : 'x mandatory',
                            overflowX: 'auto'
                        }}>
                        <DndContext sensors={sensors}
                            onDragStart={(event) => setDraggedDealId(event.active.id + '')}
                            onDragEnd={handleDragEnd}
                        >
                            {organization.dealSteps.map((step) => {
                                const deals = (dealIdsPerStep[step.id] || [])
                                    .map(dealId => dealsPerId[dealId])
                                    .filter(Boolean);
                                return <DealStepColumn key={step.id} step={step} deals={deals}
                                    layout={cardLayout}
                                    handleNewDeal={handleNewDeal}
                                    onDealArchive={handleDealArchive}
                                    onDealShare={setDealToShare}
                                    onDealSelect={deal => navigate(`${PATHS.DEALS}/${deal.id}`)}
                                    data-step-column={step.id}
                                    disableAddDeal={reachedDealLimit}
                                />
                            })}

                            {orphanedDeals.length > 0 && (
                                <DealStepColumn
                                    key="orphaned"
                                    step={{
                                        id: 'orphaned',
                                        name: text.orphanedDeals,
                                        color: '#FBEAEA'
                                    }}
                                    deals={orphanedDeals}
                                    layout={cardLayout}
                                    handleNewDeal={async () => { }} // Disable new deal creation for orphaned column
                                    onDealArchive={handleDealArchive}
                                    onDealShare={setDealToShare}
                                    onDealSelect={deal => navigate(`${PATHS.DEALS}/${deal.id}`)}
                                    data-step-column="orphaned"
                                    noNewDeals
                                />
                            )}

                            <DragOverlay dropAnimation={null}>
                                {draggedDeal ? (
                                    <DealCard layout={cardLayout} deal={draggedDeal} />
                                ) : null}
                            </DragOverlay>
                        </DndContext>
                    </div>

                    {canScrollRight && (
                        <div className="position-fixed top-50 end-0 translate-middle-y me-3 bg-white rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px', zIndex: 10, opacity: 0.8, cursor: 'pointer' }}
                            onClick={() => handleScroll('right')}>
                            <i className="bi bi-chevron-right fs-5"></i>
                        </div>
                    )}
                </div>
            </div>
            {editDeal && <DealModal
                initialFocus={newDeal ? 'name' : undefined}
                deal={editDeal}
                onDealStepChange={(dealId, stepId) => handleChangeDealStep(dealId, '', stepId, true)}
                onDealArchive={handleDealArchive}
                onClose={() => navigate('/')} />}

            <DealShareModal deal={dealToShare} onHide={() => setDealToShare(null)} />
        </>
    )
}

const getDealsPerStep = (organization: Organization, allDeals: Deal[]) => {
    const dealsPerStep: Record<string, string[]> = {};

    // Initialize arrays for all valid steps
    for (const dealStep of organization.dealSteps) {
        dealsPerStep[dealStep.id] = [];
    }

    for (const deal of allDeals) {
        if (!dealsPerStep[deal.stepId]) {
            dealsPerStep[deal.stepId] = [];
        }
        dealsPerStep[deal.stepId].push(deal.id || '');
    }
    return dealsPerStep;
}

const getOrphanedDeals = (organization: Organization, allDeals: Deal[]) => {
    const validStepIds = new Set(organization.dealSteps.map(step => step.id));
    return allDeals.filter(deal => !validStepIds.has(deal.stepId));
}
